import React from "react";
import { Formik } from "formik";
import Grid from "@material-ui/core/Grid";
import { connect } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import {
  FormikInput,
  Label,
  Selecty,
  Button,
  DatePicker,
} from "genbook-components";
import {
  orderServicesInOrderMerchantCreatedInSettingsPages,
  getLocationId,
} from "../../../../utils/helpers";
import CustomerSearch from "./CustomerSearch";
import {
  getServiceResourceAvailabilityDates,
  gotServiceResourceAvailabilityDates,
  saveWaitlist,
  updateWaitlistStepData,
} from "../../../../actions/waitlist";
import { getServiceResourceAvailabilityApi } from "../../../../actions/sagas/waitlist";
import WaitlistFormSchema from "./validation";
import { union } from "lodash";

class WaitlistForm extends React.Component {
  handleCustomerSearch = (setFieldValue) => (value) => {
    const { firstname, lastname, phone, email } = value;
    setFieldValue("firstname", firstname);
    setFieldValue("lastname", lastname);
    setFieldValue("phone", phone || "");
    setFieldValue("email", email || "");
  };

  getServiceDropdownOptions = () => {
    const {
      waitlistStepData: { serviceList },
    } = this.props;

    const dropDownList = serviceList.map((service) => ({
      ...service,
      label: service.name,
      value: service.id,
    }));
    const list = orderServicesInOrderMerchantCreatedInSettingsPages(
      dropDownList
    );
    return [{ value: "" }, ...list];
  };

  handleServiceSelection = async (service, setFieldValue) => {
    const { dispatchGetServiceResourceAvailability } = this.props;
    const defaultStaffValue = await this.getStaffListOptions(service)[0];
    setFieldValue("service", service);
    setFieldValue("staff", defaultStaffValue.value);
    this.resetTimesAndDate(setFieldValue);
    dispatchGetServiceResourceAvailability(
      service,
      defaultStaffValue.value || -1
    );
  };

  getStaffListOptions = (service = {}) => {
    const {
      waitlistStepData: { staffList = [] },
      userPermissions: {
        canEditAllBookings = false,
        displaystaffonline,
        displaynopreference,
      },
      currentlyLoggedInUserid,
    } = this.props;

    // If displaystaffonline flag is false, only show no preference option regardless of other permissions
    if (!displaystaffonline) {
      return [
        {
          value: -1,
          label: "No preference",
        },
      ];
    }

    let staffListOptions = staffList.filter((stf) =>
      stf.services.includes(Number(service))
    );

    // If user does not have permission to edit other staff calendar, can't make offline appt for other staff members
    if (!canEditAllBookings) {
      staffListOptions = staffListOptions.filter(
        (stf) => stf.userid === currentlyLoggedInUserid
      );
    }

    if (displaynopreference) {
      staffListOptions = [
        {
          id: -1,
          name: "No preference",
        },
        ...staffListOptions,
      ];
    }

    return staffListOptions.map((option) => ({
      label: option.name,
      value: option.id,
    }));
  };

  handleStaffSelection = (staffId, serviceId) => {
    const { dispatchGetServiceResourceAvailability } = this.props;
    dispatchGetServiceResourceAvailability(serviceId, staffId);
  };

  handleDateSelection = async (fieldVal, formValues, setFieldValue, field) => {
    const { service, staff, enddate } = formValues;
    const { dispatchGotServiceResourceAvailability } = this.props;
    const normalizedDate = fieldVal.format("YYYYMMDD") + "L";

    const payload = {
      data: {
        serviceId: service,
        resourceId: staff,
        day: normalizedDate,
      },
    };

    const data = await getServiceResourceAvailabilityApi(payload);
    dispatchGotServiceResourceAvailability({ payload, data });

    if (field === "startdate") {
      const firstStartTime = data.starttimes[0] || "";
      setFieldValue("starttime", firstStartTime);
      setFieldValue("enddate", fieldVal);
    }

    const lastEndTime = data.endtimes[data.endtimes.length - 1] || "";
    setFieldValue("endtime", lastEndTime);
  };

  getStartDates = () => {
    const {
      waitlistStepData: {
        bookingDates = [],
        waitlistDates = [],
        closedDates = [],
      },
    } = this.props;
    let allDates = union(bookingDates, waitlistDates, closedDates);

    return allDates.map((date) => moment(date, "YYYYMMDD"));
  };

  getEndDates = (values) => {
    const {
      waitlistStepData: {
        bookingDates = [],
        waitlistDates = [],
        closedDates = [],
      },
    } = this.props;

    if (!values.startdate) {
      return null;
    }

    let allDates = union(bookingDates, waitlistDates, closedDates);

    return allDates
      .map((date) => moment(date, "YYYYMMDD"))
      .filter((m) => m.isSameOrAfter(values.startdate));
  };

  getFriendlyTimeValue = (time) =>
    moment(time, "YYYYMMDDHHmmss").format("h:mm a");

  getFormattedTimes = (date, times = []) => {
    const doy = moment(date).dayOfYear();
    return times
      .filter((time) => moment(time, "YYYYMMDDHHmmss").dayOfYear() === doy)
      .map((time) => ({
        label: this.getFriendlyTimeValue(time),
        value: time,
      }));
  };

  handleFormSubmit = (values) => {
    const {
      dispatchSaveWaitlist,
      closeModal,
      waitlistStepData: { closedDates = [] },
    } = this.props;
    const { staff, service, ...restValues } = values;
    const { startdate, enddate } = values;
    const onclosedday =
      closedDates
        .map((date) => moment(date, "YYYYMMDD"))
        .filter((m) => m.isSameOrAfter(startdate) && m.isSameOrBefore(enddate))
        .length > 0;
    const normalizedFormValues = {
      ...restValues,
      onclosedday,
      resourceid: staff,
      serviceid: service,
      locationid: getLocationId(),
    };

    dispatchSaveWaitlist(normalizedFormValues, closeModal);
  };

  getInitialFormValues = () => ({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    service: "",
    staff: this.getStaffListOptions()[0],
    startdate: null,
    starttime: null,
    enddate: null,
    endtime: null,
  });

  resetTimesAndDate = (setFieldValue) => {
    this.props.dispatchUpdateWaitlistStepData({
      ...this.props.waitlistStepData,
      startTimes: [],
      finishTimes: [],
    });

    setFieldValue("startdate", null);
    setFieldValue("starttime", null);
    setFieldValue("enddate", null);
    setFieldValue("endtime", null);
  };

  getFinishTimes = (values) => {
    const {
      waitlistStepData: { finishTimes, startTimes = [], serviceList },
    } = this.props;
    const { service: serviceId, starttime, startdate, enddate } = values;

    if (!values.starttime) {
      return [];
    }

    if (startdate !== enddate) {
      this.getFormattedTimes(values.enddate, finishTimes);
    }

    // Filters out end times that are before selected start time
    const selectedService = serviceList.find(
      (service) => service.id == serviceId
    );

    const {
      duration,
      bufferpostduration,
      availableduration,
      secondduration,
    } = selectedService;

    const defaultDuration = [0, 0];
    const [buffHour, buffMinute] = bufferpostduration
      ? bufferpostduration.split(":")
      : defaultDuration;
    const [avHour, avMinute] = availableduration
      ? availableduration.split(":")
      : defaultDuration;
    const [secHour, secMinute] = secondduration
      ? secondduration.split(":")
      : defaultDuration;
    const [hour, minute] = duration.split(":");
    // If no start time selected in form, revert to first start time in array sent by server
    // as that is the earliest start time
    const selectedStartTime = starttime ? starttime : startTimes[0];
    const startTime = moment(selectedStartTime, "YYYYMMDDHHmmss");
    const aptHourLength =
      Number(hour) + Number(buffHour) + Number(secHour) + Number(avHour);
    const aptMinuteLength =
      Number(minute) +
      Number(buffMinute) +
      Number(secMinute) +
      Number(avMinute);
    const minimumFinishTime = startTime
      .clone()
      .add(aptHourLength, "h")
      .add(aptMinuteLength, "m");

    const endDtDoy = moment(enddate).dayOfYear();
    let times = finishTimes.filter(
      (time) =>
        moment(time, "YYYYMMDDHHmmss").dayOfYear() === endDtDoy &&
        moment(time, "YYYYMMDDHHmmss").isSameOrAfter(minimumFinishTime)
    );

    if (times.length === 0 && finishTimes.length > 0) {
      times = [finishTimes[finishTimes.length - 1]];
    }
    return this.getFormattedTimes(enddate, times);
  };

  render() {
    const {
      waitlistStepData: { startTimes, closedDates = [] },
      userPermissions: { canSearchForCustomers, displaystaffonline },
    } = this.props;

    const initialFormValues = this.getInitialFormValues();
    return (
      <Formik
        onSubmit={this.handleFormSubmit}
        enableReinitialize={true}
        initialValues={initialFormValues}
        validationSchema={WaitlistFormSchema}
        render={({ handleSubmit, setFieldValue, values, errors, touched }) => (
          <form onSubmit={handleSubmit}>
            <StyledGrid container alignItems="baseline">
              <Grid item xs={2} />
              <Grid item xs={10}>
                <CustomerSearch
                  isDisabled={!canSearchForCustomers}
                  onChange={this.handleCustomerSearch(setFieldValue)}
                  placeholder="Search for existing customer"
                />
              </Grid>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Label label="Customer" />
                </Grid>
                <Grid item xs={10}>
                  <StyledInputWrapper>
                    <FormikInput name="firstname" placeholder="First name" />
                    <FormikInput name="lastname" placeholder="Last name" />
                  </StyledInputWrapper>
                </Grid>
              </Grid>
              <Grid container alignItems="baseline">
                <Grid item xs={2}>
                  <Label label="Contact" />
                </Grid>
                <Grid item xs={10}>
                  <FormikInput name="email" placeholder="Email address" />
                </Grid>
              </Grid>
              <Grid item xs={2} />
              <Grid item xs={10}>
                <FormikInput name="phone" placeholder="Phone number" />
              </Grid>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Label label="Service" />
                </Grid>
                <Grid item xs={10}>
                  <Selecty
                    name="service"
                    options={this.getServiceDropdownOptions()}
                    onChange={(e) => {
                      this.handleServiceSelection(
                        e.target.value,
                        setFieldValue
                      );
                    }}
                    value={values.service}
                    placeholder="Select service"
                    errors={errors}
                    touched={touched}
                  />
                </Grid>
              </Grid>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Label label="Staff" />
                </Grid>
                <Grid item xs={10}>
                  <Selecty
                    isDisabled={!(displaystaffonline && !!values.service)}
                    name="staff"
                    placeholder="Select staff"
                    options={this.getStaffListOptions(values.service)}
                    onChange={(e) => {
                      setFieldValue("staff", e.target.value);
                      this.resetTimesAndDate(setFieldValue);
                      this.handleStaffSelection(e.target.value, values.service);
                    }}
                    value={values.staff}
                    errors={errors}
                    touched={touched}
                  />
                </Grid>
              </Grid>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Label label="Start" />
                </Grid>
                <StyledInputWrapper>
                  <DatePicker
                    dateFormat="ddd, MMM DD"
                    readOnly
                    name="startdate"
                    disabled={!!!(values.service && values.staff)}
                    selected={values.startdate}
                    includeDates={this.getStartDates()}
                    onChange={(val) => {
                      setFieldValue("startdate", val);

                      this.handleDateSelection(
                        val,
                        values,
                        setFieldValue,
                        "startdate"
                      );
                    }}
                    dayClassName={(date) =>
                      closedDates.includes(date.format("YYYYMMDD") + "L")
                        ? "closed"
                        : undefined
                    }
                    errors={errors}
                    touched={touched}
                    placeholderText="Start date"
                    popoverAttachment="bottom left"
                    popoverTargetAttachment="top left"
                  />
                  <Selecty
                    isDisabled={!values.startdate}
                    options={this.getFormattedTimes(
                      values.startdate,
                      startTimes
                    )}
                    name="starttime"
                    placeholder="hh:mm"
                    onChange={(e) => setFieldValue("starttime", e.target.value)}
                    value={values.starttime}
                    errors={errors}
                    touched={touched}
                  />
                </StyledInputWrapper>
              </Grid>
              <Grid container alignItems="center">
                <Grid item xs={2}>
                  <Label label="Finish" />
                </Grid>
                <StyledInputWrapper>
                  <DatePicker
                    dateFormat="ddd, MMM DD"
                    readOnly
                    name="enddate"
                    disabled={
                      !!!(values.service && values.staff && values.startdate)
                    }
                    selected={values.enddate}
                    includeDates={this.getEndDates(values)}
                    onChange={(val) => {
                      setFieldValue("enddate", val);
                      this.handleDateSelection(
                        val,
                        values,
                        setFieldValue,
                        "enddate"
                      );
                    }}
                    dayClassName={(date) =>
                      closedDates.includes(date.format("YYYYMMDD") + "L")
                        ? "closed"
                        : undefined
                    }
                    errors={errors}
                    touched={touched}
                    placeholderText="End date"
                    popoverAttachment="bottom left"
                    popoverTargetAttachment="top left"
                  />
                  <Selecty
                    isDisabled={!!!(values.starttime && values.enddate)}
                    options={this.getFinishTimes(values)}
                    name="endtime"
                    placeholder="hh:mm"
                    onChange={(e) => setFieldValue("endtime", e.target.value)}
                    value={values.endtime}
                    errors={errors}
                    touched={touched}
                  />
                </StyledInputWrapper>
              </Grid>
            </StyledGrid>
            <StyledButtonWrapper>
              <Button type="submit">Add</Button>
            </StyledButtonWrapper>
          </form>
        )}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  waitlistStepData: state.waitlistStepReducer.waitlistStepData,
  userPermissions: state.waitlistStepReducer.limitedPermissions,
  currentlyLoggedInUserid: state.waitlistStepReducer.currentlyLoggedInUserid,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchGetServiceResourceAvailability: (serviceId, resourceId, day) =>
    dispatch(getServiceResourceAvailabilityDates(serviceId, resourceId, day)),
  dispatchSaveWaitlist: (waitlistStepData, cb) =>
    dispatch(saveWaitlist(waitlistStepData, cb)),
  dispatchUpdateWaitlistStepData: (data) =>
    dispatch(updateWaitlistStepData(data)),
  dispatchGotServiceResourceAvailability: (data) =>
    dispatch(gotServiceResourceAvailabilityDates(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WaitlistForm);

const StyledGrid = styled(Grid)`
  padding: 1rem;

  > div {
    font-family: sans-serif;
    font-size: 14px;
    margin-bottom: 1.2rem;
  }

  select {
    -webkit-appearance: menulist;
    padding: 7px 10px !important;
  }
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  background: #fff;
  padding: 0.8rem 1rem;
  flex: 1;
  align-items: center;
  border-top: solid 1px #cdcdcd;

  > span {
    font-size: 14px;
    color: #333333;

    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledInputWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;

  > div {
    max-width: 47%;
  }
`;
